import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Background,
  ReactFlow,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
} from "@xyflow/react";
import {
  Box,
  Flex,
  Button,
  HStack,
  Text,
  Checkbox,
  Divider,
  CheckboxGroup,
  Stack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  PopoverCloseButton,
  Input,
  InputGroup,
  InputRightElement,
  Badge,
  InputLeftElement,
  Icon,
  useDisclosure,
  TagLabel,
  Tag,
} from "@chakra-ui/react";
import { ChevronDownIcon, SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { FaQuestionCircle } from "react-icons/fa";
import { KGStepInterface } from "./Interface";
import {
  FilterProto,
  Header,
  MetricProto,
  ObservationColumnProto,
} from "build-ts/metadata";
import { ColumnType, MetadataTypeEnum, metadataTypeEnumFromJSON } from "build-ts/enums";
import showToast from "hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";
import { getMetricById } from "services/MetricService";
import { fetchMetricsWithEdges, useKGAnalysisPlanContext } from "context/batch/KGAnalysisPlanContext";
import { AnalysisPlanProto, AnalysisStepProto, KGObservationDataProto, KGObservationProto, ObservationEditType, RecommendationProto } from "build-ts/analysis_plan";
import { getKGGrowthBenchmarks, getOperatorSymbol, mergeMetricEdgesByType } from "utils/analysisUtils";
import KGAnalysisStepNode from "./KGAnalysisStepNode";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import KGNodeConfigurator from "./KGNodeConfigurator";
import { ObservationUtils } from "utils/ObservationUtils";
import QnaAction from "../../../containers/batch/kg-analysis-plan/QnaAction";
import { GetRecommendations_Request } from "build-ts/interface";
import { getRecommendationEdits } from "services/AnalysisTreeServices";
import DataTableMKG from "components/analyse/data_table/DataTableMKG";
import { getDateFilterName } from "utils/nameUtils";
import { useURLContext } from "context/URLContext";
import { getPlan } from "services/AnalysisPlanServices";
import { getSamplePlan } from "pages/create/planUtils";

// A helper function to layout the tree and produce React Flow nodes and edges.
const spacingX = 250;
const spacingY = 150;

interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
}

// LayoutTree function that uses the nodeMap and recommendation_children_map
const layoutTree = (
  nodeId: string,
  nodeMap: Map<string, KGStepInterface>,
  x: number,
  y: number,
  processedNodeIds = new Set<string>()
): LayoutResult => {
  const node = nodeMap.get(nodeId);
  if (!node || processedNodeIds.has(nodeId)) {
    return { nodes: [], edges: [] };
  }

  // Mark this node as processed to avoid cycles
  processedNodeIds.add(nodeId);

  let nodes: Node[] = [];
  let edges: Edge[] = [];

  // Add this node
  nodes.push({
    id: node.id,
    type: "customNode",
    position: { x, y },
    // TODO: Fix this type cast.
    data: node as unknown as Record<string, unknown>,
  });

  // Process all child nodes from recommendations
  if (node.recommendation_rules && node.recommendation_children_map) {
    let totalChildren = 0;
    let processedChildren = 0;

    // Count total children
    for (const [, childIds] of Array.from(
      node.recommendation_children_map.entries()
    )) {
      totalChildren += childIds.length;
    }

    // Process each recommendation"s children
    for (const [recommendationIndex, childIds] of Array.from(
      node.recommendation_children_map.entries()
    )) {
      for (const childId of childIds) {
        // Calculate position for this child
        const childY =
          y -
          ((totalChildren - 1) * spacingY) / 2 +
          processedChildren * spacingY;
        const childX = x + spacingX;

        // Layout the child node and its descendants
        const childLayout = layoutTree(
          childId,
          nodeMap,
          childX,
          childY,
          processedNodeIds
        );

        // Add the child"s nodes and edges
        nodes = nodes.concat(childLayout.nodes);
        edges = edges.concat(childLayout.edges);

        // Create an edge from the parent to the child
        edges.push({
          id: `e-${node.id}-${childId}`,
          source: node.id,
          target: childId,
          animated: true,
          // Optional: add a label or style to indicate this is from a recommendation
          label: `Rec ${recommendationIndex + 1}`,
          style: { stroke: "#00AA11" },
        });

        processedChildren++;
      }
    }
  }

  return { nodes, edges };
};

// New function to convert a plan proto to nodeMap and tree
export const buildNodeMapFromPlan = async (
  plan: AnalysisPlanProto,
  mkgs: MetricProto[]
): Promise<{
  nodeMap: Map<string, KGStepInterface>;
  rootNode: KGStepInterface | null;
}> => {
  const nodeMap = new Map<string, KGStepInterface>();
  let rootNode: KGStepInterface | null = null;

  // Create a map of all steps by ID for quick lookup
  const stepsById = new Map<string, AnalysisStepProto>();
  if (plan.analysis_steps) {
    plan.analysis_steps.forEach((step) => {
      if (step.id) {
        stepsById.set(step.id, step);
      }
    });
  }

  // Find the root step by ID
  const rootStepId = plan.root_step_id;
  const rootStep = rootStepId ? stepsById.get(rootStepId) : null;

  if (rootStep) {
    // Process metric data using our utility function
    const processedEdges = mergeMetricEdgesByType(mkgs);
    
    // Convert headers to ObservationColumnProto objects
    const attributeColumns = ObservationUtils.getObservationColumns(processedEdges.attributeHeaders) || [];
    const measureColumns = ObservationUtils.getObservationColumns(processedEdges.measureHeaders) || [];
    const temporalColumns = ObservationUtils.getObservationColumns(processedEdges.temporalHeaders) || [];
    const metricColumns = ObservationUtils.getObservationColumns(processedEdges?.metricEdgeHeaders || []) || [];
    
    // Extract metrics from metric columns
    const availableMetrics: MetricProto[] = [];
    metricColumns.forEach(col => {
      if (col.kg_column_header?.type === MetadataTypeEnum.METRIC) {
        // Create a MetricProto from the column header
        const metric = MetricProto.create({
          header: col.kg_column_header
        });
        availableMetrics.push(metric);
      }
    });
    
    // Don't include metric columns in non_benchmarks - they should be handled separately
    const nonBenchmarks = [...attributeColumns, ...measureColumns, ...temporalColumns];
    
    const selectedBenchmarks = plan.root_observation?.benchmarks?.filter(Boolean) || [];
    const selectedColumns = plan.root_observation?.columns?.filter(Boolean) || [];
    const selectedFilters = plan.root_observation?.filters?.filter(Boolean) || [];

    // All growth(Versus) benchmarks.
    const growthBenchmarks = getKGGrowthBenchmarks(selectedFilters);
    const allBenchmarks = [...processedEdges.benchmarkEntities, ...growthBenchmarks];

    // Create metrics array - use all metrics from the array (selected metrics)
    let selectedMetrics: MetricProto[] = [...mkgs];
    
    // Also include any metrics from the root observation
    if (plan.root_observation?.metrics) {
      // Filter out duplicates
      const existingMetricIds = new Set(selectedMetrics.map(m => m.header?.id));
      plan.root_observation.metrics.forEach(m => {
        if (m.header?.id && !existingMetricIds.has(m.header.id)) {
          selectedMetrics.push(m);
          existingMetricIds.add(m.header.id);
        }
      });
    }

    // Combine available metrics: selected metrics + any additional metrics from edges
    const allAvailableMetrics = [...selectedMetrics];
    availableMetrics.forEach(metric => {
      // Add if not already present
      if (!allAvailableMetrics.some(existing => existing.header?.id === metric.header?.id)) {
        allAvailableMetrics.push(metric);
      }
    });

    // Create recommendation_edits_map (empty by default - will be populated later)
    const recommendationEditsMap = new Map<number, RecommendationProto[]>();

    rootNode = {
      id: rootStep.id || String(Date.now()),
      label: rootStep.step_label || "Root Plan",
      type: "metric",
      metric_name: plan.header?.name || "",
      // All available metrics that can be selected
      metrics: [...allAvailableMetrics],
      // Currently selected metrics
      selected_metrics: [...selectedMetrics],
      // Reference to available edges - create copies to prevent shared references
      benchmarks: [...allBenchmarks],
      non_benchmarks: [...nonBenchmarks],
      temporal_columns: [...temporalColumns],

      // Selected edges from the plan - create copies to prevent shared references
      selected_benchmarks: [...selectedBenchmarks],
      selected_columns: [...selectedColumns],
      selected_filters: [...selectedFilters],
      
      // Use the analysis_columns from the root step - create copy
      analysis_columns: [...(rootStep.analysis_columns || [])],
      
      // Set recommendations - create copy
      recommendation_rules: [...(rootStep.recommendation_rules || [])],

      // Initialize recommendation children map
      recommendation_children_map: new Map(),
      
      // Initialize recommendation edits map
      recommendation_edits_map: recommendationEditsMap,
      
      // Initialize recommendation edits array
      recommendation_edits: [],
      
      // Create KG observation - create a deep copy if it exists
      kg_observation: plan.root_observation ? KGObservationProto.create({
        metrics: [...(plan.root_observation.metrics || [])],
        benchmarks: [...(plan.root_observation.benchmarks || [])],
        filters: [...(plan.root_observation.filters || [])],
        columns: [...(plan.root_observation.columns || [])],
      }) : null
    };

    // Add to nodeMap
    nodeMap.set(rootNode.id, rootNode);

    // Process recommendation edits for the root node
    await processNodeRecommendationEdits(rootNode);

    // Process recommendation rules and child nodes recursively
    if (rootNode && rootNode.recommendation_rules?.length > 0) {
      for (let index = 0; index < rootNode.recommendation_rules.length; index++) {
        const rule = rootNode.recommendation_rules[index];
        if (!rootNode) continue; // Additional null check for TypeScript

        // Process child steps attached to this recommendation
        const childIds: string[] = rule.child_step_ids || [];

        // Update root node's recommendation_children_map
        if (childIds.length > 0) {
          if (!rootNode.recommendation_children_map) {
            rootNode.recommendation_children_map = new Map();
          }
          rootNode.recommendation_children_map.set(index, childIds);

          // Process each child step
          for (const childId of childIds) {
            const childStep = stepsById.get(childId);
            if (childStep && rootNode) {
              // Process this child and all its descendants recursively
              await processChildNode(childStep, rootNode, index, nodeMap, stepsById);
            }
          }
        }
      }
    }
  }

  return { nodeMap, rootNode };
};

// Helper function to process child nodes recursively
const processChildNode = async (
  childStep: AnalysisStepProto,
  parentNode: KGStepInterface,
  recommendationIndex: number,
  nodeMap: Map<string, KGStepInterface>,
  stepsById: Map<string, AnalysisStepProto>
) => {
  if (!childStep.id) return; // Skip if no ID

  // Check if we've already processed this node
  if (nodeMap.has(childStep.id)) {
    return;
  }
  
  // Extract observation edits by type
  const addColumns: ObservationColumnProto[] = [];
  const removeColumns: ObservationColumnProto[] = [];
  
  const addMetrics: MetricProto[] = [];
  const removeMetrics: MetricProto[] = [];
  
  const addFilters: FilterProto[] = [];
  const removeFilters: FilterProto[] = [];
  
  const addBenchmarks: BenchmarkEntityProto[] = [];
  const removeBenchmarks: BenchmarkEntityProto[] = [];
  
  // Process each observation edit
  childStep.observation_edits?.forEach(edit => {
    switch (edit.edit_type) {
      case ObservationEditType.ADD_COLUMN:
        if (edit.column) addColumns.push(edit.column);
        break;
      case ObservationEditType.REMOVE_COLUMN:
        if (edit.column) removeColumns.push(edit.column);
        break;
      case ObservationEditType.ADD_FILTER:
        if (edit.filter) addFilters.push(edit.filter);
        break;
      case ObservationEditType.REMOVE_FILTER:
        if (edit.filter) removeFilters.push(edit.filter);
        break;
      case ObservationEditType.ADD_BENCHMARK:
        if (edit.benchmark) addBenchmarks.push(edit.benchmark);
        break;
      case ObservationEditType.REMOVE_BENCHMARK:
        if (edit.benchmark) removeBenchmarks.push(edit.benchmark);
        break;
      case ObservationEditType.ADD_METRIC:
        if (edit.metric) addMetrics.push(edit.metric);
        break;
      case ObservationEditType.REMOVE_METRIC:
        if (edit.metric) removeMetrics.push(edit.metric);
        break;
    }
  });
  
  // Reconstruct selected items based on parent
  // In a child node, we start with parent's selections and apply the edits
  let selectedColumns = [...(parentNode.selected_columns || [])];
  let childSelectedMetrics = [...(parentNode.selected_metrics || [])];
  let selectedBenchmarks = [...(parentNode.selected_benchmarks || [])];
  let selectedFilters = [...(parentNode.selected_filters || [])];
  
  // Apply column edits
  if (addColumns.length) {
    const addColumnsObjects = addColumns.map(col => {
      // Ensure column has proper kg_column_header
      if (!col.kg_column_header && col.logical_column_header) {
        return ObservationColumnProto.create({
          column_id: col.column_id || "",
          kg_column_header: col.logical_column_header, // Use logical header as fallback
          logical_column_header: col.logical_column_header
        });
      }
      return col;
    });
    selectedColumns = [...selectedColumns, ...addColumnsObjects];
  }
  if (removeColumns.length) {
    const removeColumnIds = new Set(removeColumns
      .map(col => (col?.column_id || col?.kg_column_header?.id || ""))
      .filter(id => id !== ""));
    selectedColumns = selectedColumns?.filter(col => !removeColumnIds.has(col?.column_id || ""));
  }
  
  // Apply metric edits
  if (addMetrics.length) {
    childSelectedMetrics = [...childSelectedMetrics, ...addMetrics];
  }
  if (removeMetrics.length) {
    const removeMetricIds = new Set(removeMetrics.map(m => m.header?.id));
    childSelectedMetrics = childSelectedMetrics?.filter(m => !removeMetricIds.has(m.header?.id));
  }
  
  // Ensure all child metrics have edges loaded (similar to handleAddChildNode)
  childSelectedMetrics = await Promise.all(childSelectedMetrics?.map(async (metric) => {
    if (!metric.edges || metric.edges.length === 0) {
      const updatedMetric = await getMetricById(metric.header?.id!, true /* include edges */);
      return updatedMetric || metric;
    }
    return metric;
  }));
  
  // Apply benchmark edits
  if (addBenchmarks.length) {
    selectedBenchmarks = [...selectedBenchmarks, ...addBenchmarks];
  }
  if (removeBenchmarks.length) {
    const removeBenchmarkIds = new Set(removeBenchmarks.map(b => b.header?.id));
    selectedBenchmarks = selectedBenchmarks?.filter(b => !removeBenchmarkIds.has(b.header?.id));
  }
  
  // Apply filter edits
  if (addFilters.length) {
    selectedFilters = [...selectedFilters, ...addFilters];
  }
  if (removeFilters.length) {
    // Filters might have filter_id not id
    const removeFilterIds = new Set(removeFilters.map(f => f.filter_id || ""));
    selectedFilters = selectedFilters?.filter(f => !removeFilterIds.has(f.filter_id || ""));
  }
  
  // Calculate available options based on selected metrics
  const processedEdges = mergeMetricEdgesByType(childSelectedMetrics);
  
  // Get columns directly from processed edges
  const attributeColumns = ObservationUtils.getObservationColumns(processedEdges?.attributeHeaders) || [];
  const measureColumns = ObservationUtils.getObservationColumns(processedEdges?.measureHeaders) || [];
  const temporalColumns = ObservationUtils.getObservationColumns(processedEdges?.temporalHeaders) || [];
  
  // Create available metrics: include the selected metrics (with full data) plus any additional metrics from processed edges
  const availableMetrics: MetricProto[] = [...childSelectedMetrics];
  
  // Add any additional metrics from processed edge headers that aren't already in selected metrics
  processedEdges?.metricEdgeHeaders?.forEach(header => {
    // Check if this metric is already in availableMetrics
    if (!availableMetrics.some(existing => existing.header?.id === header.id)) {
      availableMetrics.push(MetricProto.create({ header: header }));
    }
  });
  
  // Get benchmarks from processed edges and add growth benchmarks
  const availableBenchmarks = [...(processedEdges?.benchmarkEntities || [])];
  // Add growth benchmarks from the growth benchmark utility
  const growthBenchmarks = getKGGrowthBenchmarks();
  availableBenchmarks.push(...growthBenchmarks);

  // Combine all available columns for non_benchmarks
  const allAvailableColumns = [
    ...attributeColumns,
    ...measureColumns,
    ...temporalColumns
  ];

  // Create a KG observation for this node.
  const kgObservation = KGObservationProto.create({
    metrics: [...childSelectedMetrics],
    benchmarks: selectedBenchmarks,
    filters: selectedFilters,
    columns: selectedColumns,
  });
  
  // Get recommendation edits from parent for this child - just get from parent's map
  const parentRecommendationEdits = parentNode.recommendation_edits_map?.get(recommendationIndex) || [];
  
  // Create a node for this child
  const childNode: KGStepInterface = {
    id: childStep.id,
    label: childStep?.step_label || "Child Step",
    type: "transform",

    // Use available metrics calculated from processed edges
    metrics: [...availableMetrics],
    // Use the selected metrics (may have been modified by edits)
    selected_metrics: [...childSelectedMetrics],
    metric_name: parentNode.metric_name,

    // Use calculated available options based on processed edges
    benchmarks: [...availableBenchmarks],
    non_benchmarks: [...allAvailableColumns],
    temporal_columns: [...temporalColumns],
    
    // Set parent node ID for derivation
    parent_node_id: parentNode.id,

    // Set selected items (keep as they are from observation edits)
    selected_columns: selectedColumns,
    selected_benchmarks: selectedBenchmarks,
    selected_filters: selectedFilters,
        
    // Use analysis_columns from the step
    analysis_columns: childStep.analysis_columns || [],
    
    // Set recommendations
    recommendation_rules: childStep.recommendation_rules || [],

    // Initialize recommendation children map
    recommendation_children_map: new Map(),
    
    // Initialize recommendation edits map for this child
    recommendation_edits_map: new Map(),
    
    // Store the accumulated recommendation edits from parent
    recommendation_edits: [...(parentNode.recommendation_edits || []), 
    ...parentRecommendationEdits],
    
    // Set KG observation
    kg_observation: kgObservation,
  };

  // Add to nodeMap
  nodeMap?.set(childNode.id, childNode);

  // Process recommendation edits for this child node
  await processNodeRecommendationEdits(childNode);

  // Process child's recommendation rules recursively
  if (childNode?.recommendation_rules?.length > 0) {
    for (let index = 0; index < childNode.recommendation_rules.length; index++) {
      const rule = childNode.recommendation_rules[index];
      const childIds: string[] = rule.child_step_ids || [];
      
      // Update child node's recommendation_children_map
      if (childIds.length > 0) {
        if (!childNode?.recommendation_children_map) {
          childNode.recommendation_children_map = new Map();
        }
        childNode?.recommendation_children_map?.set(index, childIds);
        
        // Process each child step recursively
        for (const childId of childIds) {
          const nextChildStep = stepsById.get(childId);
          if (nextChildStep) {
            await processChildNode(nextChildStep, childNode, index, nodeMap, stepsById);
          }
        }
      }
    }
  }
};

// Helper function to process recommendation edits for a node
const processNodeRecommendationEdits = async (node: KGStepInterface): Promise<void> => {
  if (!node.recommendation_rules || node.recommendation_rules.length === 0) {
    return;
  }

  // Initialize recommendation_edits_map if it doesn't exist
  if (!node.recommendation_edits_map) {
    node.recommendation_edits_map = new Map();
  }

  // Create KG observation for this node
  const kgObservation = KGObservationProto.create({
    metrics: node.metrics || [],
    benchmarks: node.selected_benchmarks || [],
    filters: node.selected_filters || [],
    columns: node.selected_columns?.filter(col => 
      col.kg_column_header?.type !== MetadataTypeEnum.METRIC) || [],
  });

  // Process each recommendation rule
  for (let index = 0; index < node.recommendation_rules.length; index++) {
    const rule = node.recommendation_rules[index];
    
    if (rule?.selections && rule.selections.length > 0) {
      try {
        // Get recommendation edits from API
        const recommendationEdits = await getRecommendationEdits(
          GetRecommendations_Request.create({
            kg_observation: kgObservation,
            selection_criteria: rule.selections[0],
          })
        );
        
        if (recommendationEdits?.recommendations) {
          // Store the recommendation edits in the map
          node.recommendation_edits_map.set(index, recommendationEdits.recommendations);
        } else {
          // Store empty array as fallback
          node.recommendation_edits_map.set(index, []);
        }
      } catch (error) {
        console.error(`Error fetching recommendation edits for node ${node.label}, recommendation ${index}:`, error);
        // Store empty array as fallback
        node.recommendation_edits_map.set(index, []);
      }
    } else {
      // Store empty array if no selection criteria
      node.recommendation_edits_map.set(index, []);
    }
  }
};

const KGAnalysisPlan: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    metrics,
    setMetrics,
    selectedMetricHeader,
    metricList,
    executeKGAnalysisPlanHandler,
    setSelectedMetricHeader,
    plan,
    setPlan,
    nodeMap,
    setNodeMap,
    configurator,
    setSelectedNodeId,
    saveKGAnalysisPlanHandler,
    observationData,
    setObservationData,
    selectedStepIndex,
    analysisActionRequests,
    setCurrentPlanHeader
  } = useKGAnalysisPlanContext();
  // Get metadataId and metadataType from URL.
  const { metadataId, metadataType } = useURLContext();
  // QnA modal state.
  const qnaModal = useDisclosure();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const [metricSearchTerm, setMetricSearchTerm] = useState<string>("");
  
  // Filter metrics based on search term
  const filteredMetrics = useMemo(() => {
    if (!metricSearchTerm) return metricList;
    
    return metricList?.filter(metric => 
      metric.name?.toLowerCase().includes(metricSearchTerm.toLowerCase())
    );
  }, [metricList, metricSearchTerm]);
  
  // Handle search input change
  const handleMetricSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMetricSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (metadataId && metadataTypeEnumFromJSON(metadataType) === MetadataTypeEnum.ANALYSIS_PLAN) {
      // Fetch the analysis plan.
      const fetchPlan = async () => {
        const analysisPlan = await getPlan(metadataId);
        if (analysisPlan) {
          setCurrentPlanHeader(analysisPlan?.header);
          // Get all metric IDs from the plan's root observation
          const metricIds: Set<string> = new Set();
          // Check for metrics in root_observation
          if (analysisPlan.root_observation?.metrics) {
            analysisPlan.root_observation.metrics.forEach(metric => {
              if (metric.header?.id) {
                metricIds.add(metric.header.id);
              }
            });
          }      
          // If still no metrics found, show error
          if (metricIds.size === 0) {
            showToast("Plan has no valid metrics", "", "error");
            return null;
          }
          const mkgs = await fetchMetricsWithEdges(Array.from(metricIds));
          mkgs?.forEach(metric => {
            metrics.set(metric.header?.id!, metric);
          });
          setMetrics(metrics);
          // Extract headers from mkgs.
          const metricHeaders = mkgs?.map(data => data.header)
            ?.filter((header): header is Header => header !== undefined);
          
          // Update context with selected metric headers
          setSelectedMetricHeader(metricHeaders);
          const { nodeMap: newNodeMap, rootNode } = await buildNodeMapFromPlan(analysisPlan, mkgs);
          setPlan(rootNode);
          setNodeMap(newNodeMap);
        }
      };
      fetchPlan();
    }
  }, [metadataId, metadataType]);

  // Update React Flow nodes and edges whenever the tree or nodeMap changes
  useEffect(() => {
    if (plan && nodeMap.size > 0) {
      const layout = layoutTree(plan.id, nodeMap, 50, 200);
      setNodes(layout.nodes);
      setEdges(layout.edges);
    } else {
      // Clear nodes and edges if there's no tree or nodeMap is empty
      setNodes([]);
      setEdges([]);
    }
  }, [plan, nodeMap, setNodes, setEdges]);

  // Loading state for Metric change.
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Loading state for KG Plan execution.
  const [isExecutingKG, setIsExecutingKG] = useState<boolean>(false);
  // Loading state for Plan loading.
  const [isLoadingPlan, setIsLoadingPlan] = useState<boolean>(false);


  // Helper function to fetch metric and model data
  const fetchMetric = async (metricId: string): Promise<
  { metricData: MetricProto | null }> => {
    // Fetch MKG.
    const metricData = await getMetricById(metricId, true /* Include edges */);
    if (!metricData || !metricData?.header) {
      showToast(`Failed to fetch metric with ID: ${metricId}`, "", "error");
      return { metricData: null };
    }

    // Update metrics map
    const metricsMap = new Map<string, MetricProto>(metrics);
    metricsMap.set(metricId, metricData);
    setMetrics(metricsMap);

    return { metricData };
  };

  // Modified to handle multiple metrics
  const createRootNode = (metricDataArray: MetricProto[]): KGStepInterface => {
    if (!metricDataArray || metricDataArray.length === 0) {
      throw new Error("No metrics provided to create root node");
    }
    
    // Process multiple metric edges using our utility function
    const processedEdges = mergeMetricEdgesByType(metricDataArray);
    
    // Convert headers to ObservationColumnProto objects
    const attributeColumns = ObservationUtils.getObservationColumns(processedEdges?.attributeHeaders || []) || [];
    const measureColumns = ObservationUtils.getObservationColumns(processedEdges?.measureHeaders || []) || [];
    const temporalColumns = ObservationUtils.getObservationColumns(processedEdges?.temporalHeaders || []) || [];
    const metricColumns = ObservationUtils.getObservationColumns(processedEdges?.metricEdgeHeaders || []) || [];
    
    // Extract metrics from metric columns
    const availableMetrics: MetricProto[] = [];
    metricColumns.forEach(col => {
      if (col.kg_column_header?.type === MetadataTypeEnum.METRIC) {
        // Create a MetricProto from the column header
        const metric = MetricProto.create({
          header: col.kg_column_header
        });
        availableMetrics.push(metric);
      }
    });
    
    // Don't include metric columns in non_benchmarks - they should be handled separately
    const nonBenchmarks = [...attributeColumns, ...measureColumns, ...temporalColumns];
    
    // Get growth benchmarks
    const growthBenchmarks = getKGGrowthBenchmarks();
    // Combine with benchmark entities from metrics
    const allBenchmarks = [...(processedEdges?.benchmarkEntities || []), ...growthBenchmarks];
    
    // Create metrics array with all selected metrics (initially selected from the input)
    const selectedMetricsArray = metricDataArray.filter(Boolean);
    
    // Combine available metrics: input metrics + any additional metrics from edges
    const allAvailableMetrics = [...selectedMetricsArray];
    availableMetrics.forEach(metric => {
      // Add if not already present
      if (!allAvailableMetrics.some(existing => existing.header?.id === metric.header?.id)) {
        allAvailableMetrics.push(metric);
      }
    });
    
    // Determine node name (use first metric if multiple selected)
    const primaryMetric = metricDataArray[0];
    
    // Create KG observation for the node
    const kgObservation = KGObservationProto.create({
      metrics: [...selectedMetricsArray],
      benchmarks: [],
      filters: [],
      columns: []
    });
    
    // Create and return the root node
    return {
      id: uuidv4(),
      label: metricDataArray.length === 1 
        ? (primaryMetric?.header?.name || "Root Metric")
        : `Combined Metrics (${metricDataArray.length})`,
      type: "metric",
      metric_name: metricDataArray.length === 1 
        ? (primaryMetric?.header?.name || "")
        : `${metricDataArray.length} Metrics`,
      // All available metrics that can be selected
      metrics: [...allAvailableMetrics],
      // Currently selected metrics 
      selected_metrics: [...selectedMetricsArray],

      // Available options for UI selection
      benchmarks: allBenchmarks,
      non_benchmarks: nonBenchmarks,
      temporal_columns: temporalColumns,
      
      // Initialize empty selections
      selected_columns: [],
      selected_benchmarks: [],
      selected_filters: [],
      
      // No parent for root node
      parent_node_id: undefined,
      
      // Initialize analysis columns array
      analysis_columns: [],
      
      // Initialize with empty recommendation rules and maps
      recommendation_rules: [],
      recommendation_children_map: new Map(),
      recommendation_edits_map: new Map(),
      recommendation_edits: [],
      
      // Set the KG observation
      kg_observation: kgObservation
    };
  };

  // Handle metric selection from dropdown - updated for multi-select
  const handleMetricChange = async (event: React.ChangeEvent<HTMLInputElement>, metricId: string) => {
    const isChecked = event.target.checked;
    
    // Update selected metrics in context
    if (isChecked) {
      // Add this metric
      setIsLoading(true);
      
      // Fetch the metric data
      const { metricData } = await fetchMetric(metricId);
      
      if (metricData) {
        // Get existing selected metrics
        const selectedMetrics = Array.from(metrics.values()).filter(m => 
          m.header && selectedMetricHeader.some(h => h.id === m.header?.id)
        );
        
        // Add this metric to the list
        if (metricData && !selectedMetrics.some(m => m.header?.id === metricData.header?.id)) {
          selectedMetrics.push(metricData);
        }
        
        // Set all selected metric headers
        const updatedHeaders = selectedMetrics.map(m => m.header).filter(Boolean) as Header[];
        setSelectedMetricHeader(updatedHeaders);
        
        // If this is the first selection, initialize the plan
        if (selectedMetrics.length > 0) {
          const rootNode = createRootNode(selectedMetrics);
          setPlan(rootNode);
          const newNodeMap = new Map<string, KGStepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
        }
      }
      
      setIsLoading(false);
    } else {
      // Remove this metric
      const currentSelectedHeaders = selectedMetricHeader || [];
      const updatedHeaders = currentSelectedHeaders.filter(header => header.id !== metricId);
      setSelectedMetricHeader(updatedHeaders);
      
      // If there are still selected metrics, update the plan
      if (updatedHeaders.length > 0) {
        const selectedMetrics = Array.from(metrics.values()).filter(m => 
          m.header && updatedHeaders.some(h => h.id === m.header?.id)
        );
        
        if (selectedMetrics.length > 0) {
          const rootNode = createRootNode(selectedMetrics);
          setPlan(rootNode);
          const newNodeMap = new Map<string, KGStepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
        }
      } else {
        // If no metrics are selected, clear the plan
        setPlan(null);
        setNodeMap(new Map());
      }
    }
  };

  // Create a wrapper function for the Load Plan button
  const onLoadPlanClick = () => {
    handleLoadSamplePlan();
  };

  // Handler to load a plan from JSON
  const handleLoadSamplePlan = async () => {
    setIsLoadingPlan(true);

    if (!getSamplePlan) {
      showToast("No plan file configured for this environment.", "", "warning");
      setIsLoadingPlan(false);
      return;
    }

    try {
      const plan = AnalysisPlanProto.fromJSON(getSamplePlan);
      
      // Get all metric IDs from the plan's root observation
      const metricIds: Set<string> = new Set();
      
      // Check for metrics in root_observation
      if (plan.root_observation?.metrics) {
        plan.root_observation.metrics.forEach(metric => {
          if (metric.header?.id) {
            metricIds.add(metric.header.id);
          }
        });
      }
      
      // If no metrics found in root_observation, try looking for a single metric
      if (metricIds.size === 0 && plan.metric?.header?.id) {
        metricIds.add(plan.metric.header.id);
      }
      
      // If still no metrics found, show error
      if (metricIds.size === 0) {
        showToast("Plan has no valid metrics", "", "error");
        setIsLoadingPlan(false);
        return;
      }
      
      // Fetch all metrics found in the plan
      const metricPromises = Array.from(metricIds).map(id => fetchMetric(id));
      const metricResults = await Promise.all(metricPromises);
      
      // Filter out null results
      const validMetricData = metricResults
        .map(result => result.metricData)
        .filter((data): data is MetricProto => data !== null);
      
      // If no valid metrics could be fetched, show error
      if (validMetricData.length === 0) {
        showToast("Failed to fetch metrics for the plan", "", "error");
        setIsLoadingPlan(false);
        return;
      }
      
      // Extract headers from valid metrics
      const metricHeaders = validMetricData
        .map(data => data.header)
        .filter((header): header is Header => header !== undefined);
      
      // Update context with selected metric headers
      setSelectedMetricHeader(metricHeaders);
      
      // Build node map from the plan using all fetched metrics
      const { nodeMap: newNodeMap, rootNode } = await buildNodeMapFromPlan(plan, validMetricData);
      
      // Update plan and node map in context
      setPlan(rootNode);
      setNodeMap(newNodeMap);
      showToast(
        "Plan loaded with " + metricHeaders.length + " metric" + (metricHeaders.length > 1 ? "s" : "") + ".", 
        "", 
        "success"
      );
    } catch (error) {
      console.error("Error loading plan:", error);
      showToast("Failed to load plan", "", "error");
    } finally {
      setIsLoadingPlan(false);
    }
  };

  // Execute the KG analysis plan
  const handleExecuteKGPlan = async () => {
    try {
      setIsExecutingKG(true);
      await executeKGAnalysisPlanHandler();
    } catch (error) {
      console.error("Error executing KG analysis plan:", error);
    } finally {
      setIsExecutingKG(false);
    }
  };

  // Save the plan.
  const handleSavePlan = async () => {
    saveKGAnalysisPlanHandler();
  };

  // Handle node click to open configurator
  const handleNodeClick = (_: React.MouseEvent, node: Node) => {
    setSelectedNodeId(node.id);
    configurator.onOpen();
  };

  // Handle select all metrics
  const handleSelectAll = () => {
    // If all filtered metrics are selected, deselect all
    const allFilteredMetricIds = new Set(filteredMetrics.map(m => m.id || ""));
    const selectedFilteredCount = selectedMetricHeader.filter(h => 
      allFilteredMetricIds.has(h.id || "")
    ).length;
    
    if (selectedFilteredCount === filteredMetrics.length) {
      // Deselect only filtered metrics (keep others selected)
      const newSelectedHeaders = selectedMetricHeader.filter(h => 
        !allFilteredMetricIds.has(h.id || "")
      );
      setSelectedMetricHeader(newSelectedHeaders);
      
      // Update the plan if needed
      if (newSelectedHeaders.length > 0) {
        const selectedMetrics = Array.from(metrics.values()).filter(m => 
          m.header && newSelectedHeaders.some(h => h.id === m.header?.id)
        );
        
        if (selectedMetrics.length > 0) {
          const rootNode = createRootNode(selectedMetrics);
          setPlan(rootNode);
          const newNodeMap = new Map<string, KGStepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
        }
      } else {
        // If no metrics are selected, clear the plan
        setPlan(null);
        setNodeMap(new Map());
      }
    } else {
      // Select all filtered metrics
      // First get existing selected metrics not in the filtered list
      const existingSelected = selectedMetricHeader.filter(h => 
        !allFilteredMetricIds.has(h.id || "")
      );
      
      // Add all filtered metrics to the selection
      const metricsToSelect = filteredMetrics
        .map(m => m.id)
        .filter((id): id is string => id !== undefined);
      
      const metricsToFetch = metricsToSelect.filter(id => 
        !metrics.has(id)
      );
      
      // Fetch any metrics we don't have data for yet
      const fetchAllMetrics = async () => {
        setIsLoading(true);
        
        const fetchPromises = metricsToFetch.map(id => fetchMetric(id));
        await Promise.all(fetchPromises);
        
        // Now get all the metric data objects
        const selectedMetricData = metricsToSelect
          .map(id => metrics.get(id))
          .filter(Boolean) as MetricProto[];
        
        // Combine with existing selected metrics not in filtered list
        const allHeaders = [...existingSelected];
        
        // Add headers from the fetched metrics
        selectedMetricData.forEach(m => {
          if (m.header && !allHeaders.some(h => h.id === m.header?.id)) {
            allHeaders.push(m.header);
          }
        });
        
        // Update selected headers
        setSelectedMetricHeader(allHeaders);
        
        // Update the plan
        if (selectedMetricData.length > 0) {
          const rootNode = createRootNode(selectedMetricData);
          setPlan(rootNode);
          const newNodeMap = new Map<string, KGStepInterface>();
          newNodeMap.set(rootNode.id, rootNode);
          setNodeMap(newNodeMap);
        }
        
        setIsLoading(false);
      };
      
      fetchAllMetrics();
    }
  };
  
  // Handle clear all selections
  const handleClearSelections = () => {
    setSelectedMetricHeader([]);
    setPlan(null);
    setNodeMap(new Map());
  };
  
  // Check if all filtered metrics are selected
  const areAllFilteredMetricsSelected = useMemo(() => {
    if (filteredMetrics?.length === 0) return false;
    
    return filteredMetrics?.every(metric => 
      selectedMetricHeader.some(h => h.id === metric.id)
    );
  }, [filteredMetrics, selectedMetricHeader]);

  const renderFilters = (observationData: KGObservationDataProto | null) => {
    if(!observationData) return null;

    let filters: FilterProto[] = [];
  
    // If not, check if node.current_node.kg_observation is present
    if (observationData?.kg_observation) {
      filters = observationData.kg_observation.filters || [];
    }

    if(filters?.length === 0 || !filters) return null;

    const groupedFilters = filters.reduce((acc, filter) => {
      let colunName = filter?.kg_column_header?.name || "";
      let key = "";
      if(filter?.logical_column_header?.column_type === ColumnType.TEMPORAL) {
        key = "DATE_FILTERS";
      } else {
        key = colunName;
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(filter);
      return acc;
    }, {} as Record<string, FilterProto[]>);
    
  
    return (
      <Box
        overflowX="auto"
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": {
            height: "3px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        <HStack spacing={2} py={1}>
          {Object.entries(groupedFilters).map(([key, filtersGroup], index) => {
            // Special handling for date filters
            if (key === "DATE_FILTERS") {
              return (
                <Tag
                  key={index}
                  size="sm"
                  borderRadius="md"
                  variant="solid"
                  bg="gray.50"
                  color="gray.800"
                  borderWidth="1px"
                  borderColor="gray.300"
                  p={1}
                  minW="fit-content"
                >
                  <TagLabel 
                    fontSize="sm"
                    textAlign="left"
                    whiteSpace="normal"
                  >
                    <Box as="span" fontWeight="700">
                      Date
                    </Box>
                    <Box as="span" fontWeight="normal">
                      {filtersGroup.map((filter, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && " | "}
                          {" "}
                          {getOperatorSymbol(filter?.operator!)}
                          {" "}
                          {filter?.values?.length > 0 
                            ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                            : getDateFilterName(filter?.date_filter!)}
                        </React.Fragment>
                      ))}
                    </Box>
                  </TagLabel> 
                </Tag>
              );
            }
            
            // Regular handling for non-date filters
            return filtersGroup.map((filter, i) => (
              <Tag
                key={`${index}-${i}`}
                size="sm"
                borderRadius="md"
                variant="solid"
                bg="gray.50"
                color="gray.800"
                borderWidth="1px"
                borderColor="gray.300"
                p={1}
                minW="fit-content"
              >
                <TagLabel 
                  fontSize="sm"
                  textAlign="left"
                  whiteSpace="normal"
                >
                  <Box as="span" fontWeight="700">
                    {filter?.kg_column_header?.name}
                  </Box>
                  <Box as="span" fontWeight="normal">
                    {" "}
                    {getOperatorSymbol(filter?.operator!)}
                    {" "}
                    {filter?.values?.length > 0 
                      ? filter.values?.map((value: any) => value.toString().trim()).join(", ")
                      : getDateFilterName(filter?.date_filter!)}
                  </Box>
                </TagLabel> 
              </Tag>
            ));
          })}
        </HStack>
      </Box>
    );
  };

  return (
    <Box width="100%" height="100%" ref={containerRef}>
      <Flex direction="column" height="100%">
        <HStack
          spacing={4}
          p={2}
          bg="white"
          borderBottom="1px"
          borderColor="gray.200"
        >
          {/* Replace the existing metrics selection with improved UI */}
          <Box width="300px">
            <Popover placement="bottom-start" closeOnBlur={false}>
              <PopoverTrigger>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input 
                    placeholder="Select metrics..." 
                    value={selectedMetricHeader?.length ? `${selectedMetricHeader.length} metric(s) selected` : ""} 
                    readOnly 
                    cursor="pointer"
                    isDisabled={isLoading || isExecutingKG || isLoadingPlan}
                  />
                  <InputRightElement>
                    <Icon as={ChevronDownIcon} color="gray.500" />
                  </InputRightElement>
                </InputGroup>
              </PopoverTrigger>
              <PopoverContent width="350px">
                <PopoverHeader fontWeight="semibold">
                  Select Metrics
                  <PopoverCloseButton />
                </PopoverHeader>
                <PopoverBody maxH="300px" overflowY="auto">
                  {/* Search filter for metrics */}
                  <Input
                    placeholder="Search metrics..."
                    size="sm"
                    mb={2}
                    variant="filled"
                    value={metricSearchTerm}
                    onChange={handleMetricSearch}
                  />
                  
                  {/* Select All checkbox */}
                  <Checkbox 
                    isChecked={areAllFilteredMetricsSelected}
                    onChange={handleSelectAll}
                    mb={2}
                    fontWeight="semibold"
                    size="sm"
                    isDisabled={filteredMetrics?.length === 0 || isLoading}
                  >
                    {areAllFilteredMetricsSelected ? "Deselect All" : "Select All"} 
                    {metricSearchTerm ? "Filtered" : ""}
                  </Checkbox>
                  
                  <CheckboxGroup colorScheme="teal">
                    <Stack spacing={1} mt={2}>
                      {filteredMetrics?.map((metric) => (
                        <Checkbox 
                          key={metric.id} 
                          value={metric.id || ""}
                          isChecked={selectedMetricHeader?.some(h => h.id === metric.id)}
                          onChange={(e) => handleMetricChange(e, metric.id || "")}
                          isDisabled={isLoading || isExecutingKG || isLoadingPlan}
                        >
                          {metric.name || "Unnamed Metric"}
                        </Checkbox>
                      ))}
                      {filteredMetrics?.length === 0 && (
                        <Text fontSize="sm" color="gray.500" p={2}>
                          No metrics found matching "{metricSearchTerm}"
                        </Text>
                      )}
                    </Stack>
                  </CheckboxGroup>
                </PopoverBody>
                <PopoverFooter display="flex" justifyContent="space-between" alignItems="center">
                  {selectedMetricHeader?.length > 0 && (
                    <Box>
                      <Badge colorScheme="teal" borderRadius="full" px="2">
                        {selectedMetricHeader.length} selected
                      </Badge>
                    </Box>
                  )}
                  {selectedMetricHeader?.length > 0 && (
                    <Button 
                      size="xs" 
                      colorScheme="red" 
                      variant="outline"
                      onClick={handleClearSelections}
                      isDisabled={isLoading}
                    >
                      Clear All
                    </Button>
                  )}
                </PopoverFooter>
              </PopoverContent>
            </Popover>
          </Box>
          
          <Divider orientation="vertical" height="40px" />
          {/* Rest of HStack content */}
          <Button
            colorScheme="teal"
            onClick={onLoadPlanClick}
            isDisabled={isLoading || isExecutingKG || isLoadingPlan}
            isLoading={isLoadingPlan}
            loadingText="Loading Plan..."
          >
            Load Plan
          </Button>
          <Button
            colorScheme="green"
            onClick={handleExecuteKGPlan}
            isDisabled={
              !(selectedMetricHeader && selectedMetricHeader.length > 0) ||
              isLoading ||
              isExecutingKG ||
              isLoadingPlan
            }
            isLoading={isExecutingKG}
            loadingText="Executing KG..."
          >
            Execute KG Plan
          </Button>
          <Button
            colorScheme="purple"
            leftIcon={<Icon as={FaQuestionCircle} />}
            onClick={qnaModal.onOpen}
            isDisabled={
              (selectedStepIndex === null) ||
              isLoading ||
              isExecutingKG ||
              isLoadingPlan
            }
          >
            Add Questions
          </Button>

          <Button
            colorScheme="blue"
            onClick={handleSavePlan}
            isDisabled={isLoading || isExecutingKG || isLoadingPlan}
          >
            Save Plan & Update Job
          </Button>
        </HStack>
        
        {/* Data Table Section - Bottom Panel */}
        {observationData && (
          <Box
            position="absolute"
            bottom={5}
            alignContent="flex-start"
            maxWidth="70%"
            width="100%"
            maxHeight="40vh"
            height="auto"
            zIndex={2}
            bg="white"
            borderWidth="2px"
            borderRadius="md"
            borderColor="purple.700"
            boxShadow="lg"
            display="flex"
            flexDirection="column"
            onWheel={(e) => {
              e.stopPropagation();
              const target = e.currentTarget;
              target.scrollTop += e.deltaY;
            }}
          >
            <Flex justifyContent="space-between" alignItems="center" width="100%">
              <Box flex="1">
                {renderFilters(observationData?.current_node?.kg_observation_data || null)}
              </Box>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="purple"
                leftIcon={<CloseIcon p={0} ml={2} boxSize={3}/>}
                onClick={() => {
                  setObservationData(null);
                  configurator.onClose();
                }}
                p={0}
                _hover={{ bg: "purple.100" }}
              >
              </Button>
            </Flex>
            <DataTableMKG 
              node={observationData} 
              isChatOpen={false} 
              metrics={metrics} 
              treePannelLoader={false} 
              showEditSuggestions={false}
              isOverlay={true}
            />
          </Box>
        )}

        <Flex flex="1" direction="column">
          <Box flex="1" position="relative">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={{ customNode: KGAnalysisStepNode }}
              onNodeClick={handleNodeClick}
              fitView={false}
              proOptions={{ hideAttribution: true }}
              defaultViewport={{ x: 0, y: 0, zoom: 1 }}
              style={{ height: "100%", width: "100%" }}
            >
              <Background />
            </ReactFlow>
          </Box>
        </Flex>
      </Flex>
      <KGNodeConfigurator />
      
      {/* QnA Action Modal */}
      <QnaAction
        isOpen={qnaModal.isOpen}
        onClose={qnaModal.onClose}
        stepId={selectedStepIndex !== null ? analysisActionRequests[selectedStepIndex]?.step_id : undefined}
      />
    </Box>
  );
};

export default KGAnalysisPlan;
